import React, { useEffect, useRef, useState } from 'react';
import {
    TopologyLink,
    BaselineData,
    getRawBaselineLink,
    getOfflineTiming,
    resolveLinkBandwidth,
    parseCarrier,
    toShortHostname,
} from '../TopologyData';

interface Props {
    /** Links whose IS-IS adjacency dropped entirely (from computeDownLinks). */
    downLinks: TopologyLink[];
    /** Raw baseline payload — source of each link's `lastSeenAt`. */
    rawBaseline: BaselineData | null;
    /** Open the detail panel for a link when its row is clicked. */
    onSelectLink?: (linkId: string) => void;
}

/**
 * At-a-glance "how are we doing" alarm for the KE Topology header: a count of
 * backbone links currently seen down, styled like the alerts alarm (danger
 * when > 0). Clicking it opens a popover summarizing each down link with its
 * last-seen-up time and auto-removal countdown.
 */
const DownLinksAlarm: React.FC<Props> = ({
    downLinks,
    rawBaseline,
    onSelectLink,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const count = downLinks.length;
    const hasDown = count > 0;

    // Close on outside click (mirrors the header's Label Fields menu).
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                aria-label={`${count} links down`}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold
                    rounded-lg border transition-colors cursor-pointer"
                style={
                    hasDown
                        ? {
                              background: 'var(--color-semantic-bg-danger-bold)',
                              color: 'var(--color-semantic-text-inverse-danger)',
                              borderColor: 'var(--color-semantic-bg-danger-bold)',
                          }
                        : {
                              background: 'var(--color-semantic-bg-secondary)',
                              color: 'var(--color-semantic-text-secondary)',
                              borderColor: 'var(--color-semantic-border-secondary)',
                          }
                }
            >
                {hasDown ? (
                    <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                ) : (
                    <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                )}
                <span>
                    {count} {count === 1 ? 'link' : 'links'} down
                </span>
            </button>

            {open && (
                <div
                    className="absolute top-full right-0 mt-1 z-50
                        bg-semantic-bg-primary border border-semantic-border-secondary
                        rounded-xl shadow-xl w-[320px] overflow-hidden"
                >
                    <div
                        className="px-3 py-2 border-b border-semantic-border-secondary
                            flex items-center justify-between"
                    >
                        <span
                            className="text-[11px] font-semibold text-semantic-text-secondary
                                tracking-wide uppercase"
                        >
                            Down links ({count})
                        </span>
                    </div>

                    {!hasDown ? (
                        <div className="px-3 py-4 text-xs text-semantic-text-secondary text-center">
                            All backbone links are up.
                        </div>
                    ) : (
                        <div className="max-h-[280px] overflow-y-auto py-1">
                            {downLinks.map((link) => {
                                const timing = getOfflineTiming(
                                    getRawBaselineLink(rawBaseline, link.id)
                                        ?.lastSeenAt
                                );
                                const bw = resolveLinkBandwidth(link);
                                const carrier =
                                    parseCarrier(link.sourceInterfaceDesc) ??
                                    parseCarrier(link.targetInterfaceDesc);
                                return (
                                    <button
                                        key={link.id}
                                        onClick={() => {
                                            onSelectLink?.(link.id);
                                            setOpen(false);
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-semantic-bg-secondary
                                            border-b border-semantic-border-secondary last:border-b-0
                                            cursor-pointer bg-transparent"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-semibold text-semantic-text-primary truncate">
                                                {toShortHostname(link.source)}
                                                {' → '}
                                                {toShortHostname(link.target)}
                                            </span>
                                            <span className="flex items-center gap-1.5 shrink-0">
                                                {carrier && (
                                                    <span className="text-[10px] font-semibold text-semantic-text-brand uppercase tracking-wide">
                                                        {carrier}
                                                    </span>
                                                )}
                                                {bw.label && (
                                                    <span className="text-[10px] text-semantic-text-secondary">
                                                        {bw.shortLabel ?? bw.label}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        {timing && (
                                            <div className="mt-0.5 flex flex-col gap-0.5 text-[10px] text-semantic-text-secondary">
                                                <span>
                                                    Last seen up:{' '}
                                                    <span className="text-semantic-text-primary font-medium">
                                                        {timing.lastSeenAgo}
                                                    </span>{' '}
                                                    ({timing.lastSeenLabel})
                                                </span>
                                                <span>
                                                    Auto-removes:{' '}
                                                    <span className="text-semantic-text-primary font-medium">
                                                        {timing.removesInLabel}
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DownLinksAlarm;
